import classNames from "classnames";
import React from "react";
import { Trans, useTranslation } from "react-i18next";

import { cancelOrder } from "@repo/shopifyV2/api/cancelOrder";
import { OrderStatusButtonType } from "@repo/shopifyV2/types/order";
import { extractId } from "@repo/shopifyV2/utils/common";

import Button from "@atoms/Button";
import Link from "@atoms/Link";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import useOrders from "@queries/useOrders";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IProps {
  orderId: string;
  buttonType: OrderStatusButtonType;
  className?: string;
}

export default function OrderStatusButton({ orderId, buttonType, className }: IProps) {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { openModal, openToast, closeAll, setIsLoading } = useLayoutStore();
  const { customer } = useCustomerStore();

  const { refetchOrders } = useOrders();

  const handleCancelOrder = async () => {
    try {
      setIsLoading(true);
      const response = await cancelOrder({
        shop: import.meta.env.VITE_REACT_APP_SHOP_NAME as string,
        customerId: extractId(customer.id),
        orderId,
        domain: lang,
      });

      if (response?.status === 200) {
        setTimeout(async () => {
          await refetchOrders().then(() => {
            closeAll();
            setIsLoading(false);
            openToast(NOTIFICATIONS.SUCCESS_CANCEL_ORDER);
          });
        }, 1000);
      } else {
        closeAll();
        setIsLoading(false);
        openToast(NOTIFICATIONS.ERROR_CANCEL_ORDER);
      }
    } catch (error) {
      closeAll();
      setIsLoading(false);
      openToast(NOTIFICATIONS.ERROR_CANCEL_ORDER);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    }
  };

  const handleOpenCancelModal = () => {
    openModal({
      title: "Cancel Order",
      content: <Trans i18nKey="modal.cancel_order.description" />,
      confirm: {
        label: "Confirm",
        onClick: () => {
          void handleCancelOrder();
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {
          closeAll();
        },
      },
    });
  };

  return (
    <>
      {buttonType === OrderStatusButtonType.CANCEL && (
        <Button as="square" className={classNames("leading-none", className)} onClick={handleOpenCancelModal} variant="secondary">
          {t("cancel_order")}
        </Button>
      )}

      {buttonType === OrderStatusButtonType.RETURN && (
        <Link
          as="square"
          className={classNames("flex! items-center justify-center px-3! leading-none", className)}
          href={`${URLS.RETURN_REQUEST_DETAIL}/${orderId}`}
          state={{ orderId }}
          variant="secondary"
        >
          {t("request_return")}
        </Link>
      )}
    </>
  );
}
